import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { PawPrint, Camera, X, Layers, Plus, AlertCircle, Droplets } from 'lucide-react';
import { usePets } from '../../../contexts/PetsContext';
import { useFeedingGroups } from '../../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../../contexts/HydrationGroupsContext';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import { ROUTES } from '../../../config/constants';
import { formatGrams } from '../../../lib/format';
import { sumDispenserStock } from '../../../services/feedingGroupService';

/**
 * Form de creación de mascota.
 *
 * Stage 3.1: grupo y mascota se crean por separado.
 *  - Si no hay grupos en el hogar → empty-state con CTA a /groups/new.
 *  - Si hay grupos → selector + chip para "Crear grupo nuevo" (también va a /groups/new).
 *  - Al volver de /groups/new con ?selectGroup=<id>, autoseleciona ese grupo.
 *
 * Esto evita el bug pre-fix: si createPet fallaba después de createFeedingGroup,
 * quedaba un grupo huérfano y el siguiente intento creaba otro más.
 */

const SPECIES = [
  { value: 'dog', label: 'Perro' },
  { value: 'cat', label: 'Gato' },
  { value: 'other', label: 'Otro' },
];

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_DAILY_RATION: 'dailyRationGr',
  INVALID_FEEDINGS_PER_DAY: 'feedingsPerDay',
};

// Clave del draft del form en sessionStorage. Se persiste antes de navegar
// a /groups/new o /water-groups/new (cuando el usuario clickea "Crear grupo
// nuevo" desde acá) para que al volver no pierda nombre / ración / especie.
// Se limpia al guardar exitosamente o al cancelar el form.
const PETFORM_DRAFT_KEY = 'petcare:petform.draft.v1';

const PetForm = () => {
  const { createPet, uploadPetPhoto } = usePets();
  const { groups, isLoading: groupsLoading } = useFeedingGroups();
  const { groups: hydrationGroups } = useHydrationGroups();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const autoSelectGroupId = params.get('selectGroup');
  const autoSelectWaterGroupId = params.get('selectWaterGroup');

  // Inicialización: si hay un draft persistido en sessionStorage (del flujo
  // "ir a crear grupo nuevo"), lo usamos como valor inicial. Esto evita el
  // flash de form vacío que pasaría si lo restauráramos en un useEffect post-mount.
  const initialDraft = (() => {
    try {
      const raw = sessionStorage.getItem(PETFORM_DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [name, setName] = useState(initialDraft?.name ?? '');
  const [species, setSpecies] = useState(initialDraft?.species ?? 'dog');
  const [dailyRationGr, setDailyRationGr] = useState(initialDraft?.dailyRationGr ?? '');
  const [feedingsPerDay, setFeedingsPerDay] = useState(initialDraft?.feedingsPerDay ?? '2');
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // Hidratación: opt-in. Si `hydrationEnabled` está activo, el form exige
  // que el usuario seleccione un balde existente o cree uno nuevo. Si
  // viene con ?selectWaterGroup=<id> (al volver de /water-groups/new), se
  // autoselecciona ese balde y se activa el toggle.
  const [hydrationEnabled, setHydrationEnabled] = useState(
    initialDraft?.hydrationEnabled ?? false
  );
  const [selectedHydrationGroupId, setSelectedHydrationGroupId] = useState(
    initialDraft?.selectedHydrationGroupId ?? null
  );

  // Refs para que los useEffects de auto-select apliquen UNA SOLA VEZ y no
  // pisen una selección manual posterior del usuario. Sin esto, cada nuevo
  // snapshot de groups dispara el effect y revierte el click manual.
  const autoSelectAppliedRef = useRef(false);
  const autoSelectWaterAppliedRef = useRef(false);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Limpiar el draft de sessionStorage apenas lo consumimos.
  // Lo hacemos en un effect post-mount para no romper hooks order.
  useEffect(() => {
    if (initialDraft) {
      sessionStorage.removeItem(PETFORM_DRAFT_KEY);
    }
    // initialDraft se computa una vez en el useState init; no entra al deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inicialización del grupo seleccionado:
  //  - Si volvimos de /groups/new con ?selectGroup=<id> y ese grupo existe → autoseleciona UNA VEZ.
  //  - Sino, si hay grupos y no hay nada seleccionado todavía, default al primero.
  // No incluimos selectedGroupId en deps: la version funcional de setState
  // lee el valor previo sin necesidad de tenerlo en deps. Y el ref nos
  // asegura que el autoselect del query param solo aplica una vez (no pisa
  // selecciones manuales posteriores cuando llegan snapshots nuevos).
  useEffect(() => {
    if (groupsLoading) return;
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    if (
      autoSelectGroupId &&
      !autoSelectAppliedRef.current &&
      groups.some((g) => g.id === autoSelectGroupId)
    ) {
      setSelectedGroupId(autoSelectGroupId);
      autoSelectAppliedRef.current = true;
      return;
    }
    // Default al primer grupo solo si todavía no hay nada seleccionado.
    setSelectedGroupId((prev) => prev ?? groups[0].id);
  }, [groups, groupsLoading, autoSelectGroupId]);

  // Auto-select balde al volver de /water-groups/new. Mismo patrón:
  // aplica UNA SOLA VEZ vía ref, no pisa la elección manual del usuario.
  useEffect(() => {
    if (!autoSelectWaterGroupId || autoSelectWaterAppliedRef.current) return;
    if (hydrationGroups.some((g) => g.id === autoSelectWaterGroupId)) {
      setHydrationEnabled(true);
      setSelectedHydrationGroupId(autoSelectWaterGroupId);
      autoSelectWaterAppliedRef.current = true;
    }
  }, [autoSelectWaterGroupId, hydrationGroups]);

  // Persistir el draft antes de navegar a la página de crear grupo (de comida
  // o de agua). Reemplaza el <Link> que perdía todo el state al desmontar.
  const persistDraftAndNavigate = (target) => {
    try {
      sessionStorage.setItem(
        PETFORM_DRAFT_KEY,
        JSON.stringify({
          name,
          species,
          dailyRationGr,
          feedingsPerDay,
          hydrationEnabled,
          selectedHydrationGroupId,
        })
      );
    } catch {
      // sessionStorage puede fallar en modo privado/quota lleno — no es crítico.
    }
    navigate(target);
  };

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se aceptan archivos de imagen');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('La imagen supera 15 MB');
      return;
    }
    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Cálculos derivados para preview
  const dailyRationNum = Number(dailyRationGr);
  const feedingsNum = Number(feedingsPerDay);
  const portionPreview =
    dailyRationNum > 0 && feedingsNum > 0
      ? Math.round(dailyRationNum / feedingsNum)
      : null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!selectedGroupId) {
      toast.error('Elegí un grupo para la mascota');
      return;
    }

    if (hydrationEnabled && !selectedHydrationGroupId) {
      toast.error('Elegí un balde de agua o desactivá el tracking de hidratación');
      return;
    }

    try {
      setSubmitting(true);
      const result = await createPet({
        feedingGroupId: selectedGroupId,
        hydrationGroupId: hydrationEnabled ? selectedHydrationGroupId : null,
        name,
        species,
        dailyRationGr: Number(dailyRationGr),
        feedingsPerDay: Number(feedingsPerDay),
      });

      if (photoFile) {
        try {
          await uploadPetPhoto(result.petId, photoFile);
        } catch (err) {
          toast.error(
            err.message || 'Mascota creada, pero la foto no se pudo subir'
          );
        }
      }

      // Mascota creada OK → limpiar cualquier draft residual y volver al detalle.
      sessionStorage.removeItem(PETFORM_DRAFT_KEY);
      toast.success(`${name} fue agregada al hogar`);
      navigate(`/pets/${result.petId}`, { replace: true });
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo crear la mascota.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Estado: sin grupos en el hogar → empty-state con CTA.
  if (!groupsLoading && groups.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="rounded-full bg-amber-50 p-3 ring-1 ring-amber-100">
            <AlertCircle className="h-6 w-6 text-amber-600" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Falta un grupo de alimentación</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-sm">
              Antes de agregar una mascota necesitás crear un grupo. El grupo guarda el
              dispenser, la bolsa principal y las reservas. Mascotas del mismo grupo
              comparten todo eso.
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            leftIcon={Plus}
            onClick={() =>
              persistDraftAndNavigate(
                `${ROUTES.GROUP_NEW}?returnTo=${encodeURIComponent('/pets/new')}`
              )
            }
          >
            Crear primer grupo
          </Button>
          <Button
            as={Link}
            to={ROUTES.PETS}
            variant="secondary"
            size="sm"
          >
            Cancelar
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={submitting}
          className={[
            'group relative flex h-24 w-24 items-center justify-center rounded-full',
            'bg-slate-100 ring-2 ring-slate-200 ring-offset-2 ring-offset-slate-50',
            'transition-colors hover:bg-slate-200 disabled:opacity-50',
            'focus:outline-none focus-visible:ring-brand-500',
          ].join(' ')}
          aria-label={photoPreviewUrl ? 'Cambiar foto' : 'Agregar foto'}
        >
          {photoPreviewUrl ? (
            <img
              src={photoPreviewUrl}
              alt=""
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-slate-400">
              <Camera className="h-7 w-7" aria-hidden="true" />
              <span className="text-[10px] font-medium uppercase tracking-wide">Foto</span>
            </div>
          )}
        </button>
        {photoFile && (
          <button
            type="button"
            onClick={clearPhoto}
            disabled={submitting}
            className={[
              'relative -ml-6 mt-1 flex h-7 w-7 items-center justify-center rounded-full',
              'bg-slate-900 text-white shadow-md hover:bg-slate-800',
              'disabled:opacity-50',
            ].join(' ')}
            aria-label="Quitar foto"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-lg bg-brand-100 p-2.5">
            <PawPrint className="h-5 w-5 text-brand-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Datos básicos</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Lo esencial para empezar
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Input
            label="Nombre"
            placeholder="Ej: Luna, Firulais, Roma..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            maxLength={40}
            autoFocus
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Especie
            </label>
            <div className="flex gap-2">
              {SPECIES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSpecies(s.value)}
                  className={[
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                    species === s.value
                      ? 'border-brand-500 bg-brand-50 text-brand-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="font-semibold text-slate-900 mb-1">Alimentación</h2>
        <p className="text-sm text-slate-500 mb-4">
          Cuánto come por día y en cuántas raciones
        </p>

        <div className="flex flex-col gap-3">
          <Input
            label="Ración diaria total (gramos)"
            type="number"
            inputMode="numeric"
            placeholder="Ej: 300"
            value={dailyRationGr}
            onChange={(e) => setDailyRationGr(e.target.value)}
            error={errors.dailyRationGr}
            min={5}
            max={5000}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Comidas por día
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setFeedingsPerDay(String(n))}
                  className={[
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                    feedingsPerDay === String(n)
                      ? 'border-brand-500 bg-brand-50 text-brand-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {n}
                </button>
              ))}
            </div>
            {errors.feedingsPerDay && (
              <p className="mt-1.5 text-sm text-status-red">
                {errors.feedingsPerDay}
              </p>
            )}
          </div>

          {portionPreview !== null && (
            <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">
              Cada ración será de aproximadamente{' '}
              <strong>{formatGrams(portionPreview)}</strong>, cada{' '}
              <strong>{Math.round(24 / feedingsNum)} h</strong>.
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="flex items-start gap-3 mb-3">
          <div className="rounded-lg bg-brand-100 p-2.5">
            <Layers className="h-5 w-5 text-brand-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Grupo de alimentación</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Las mascotas del mismo grupo comparten dispenser, bolsa y reservas.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSelectedGroupId(g.id)}
              className={[
                'flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors',
                selectedGroupId === g.id
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="flex-1">
                <span className="block text-sm font-medium text-slate-900">{g.name}</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  {(g.dispensers ?? []).length === 0
                    ? `Sin dispenser · Bolsa ${formatGrams(g.mainStockGr)}`
                    : `${g.dispensers.length === 1 ? 'Dispenser' : `Dispensers (${g.dispensers.length})`} ${formatGrams(sumDispenserStock(g.dispensers))} · Bolsa ${formatGrams(g.mainStockGr)}`}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={[
                  'ml-2 h-4 w-4 rounded-full shrink-0',
                  selectedGroupId === g.id ? 'bg-brand-600' : 'border-2 border-slate-300',
                ].join(' ')}
              />
            </button>
          ))}

          <button
            type="button"
            onClick={() =>
              persistDraftAndNavigate(
                `${ROUTES.GROUP_NEW}?returnTo=${encodeURIComponent('/pets/new')}`
              )
            }
            className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors text-left"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Crear grupo nuevo
          </button>
        </div>
      </Card>

      <Card>
        <div className="flex items-start gap-3 mb-3">
          <div className="rounded-lg bg-sky-100 p-2.5">
            <Droplets className="h-5 w-5 text-sky-700" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-slate-900">Hidratación (opcional)</h2>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={hydrationEnabled}
                  onChange={(e) => setHydrationEnabled(e.target.checked)}
                />
                <span className="relative w-10 h-6 bg-slate-200 peer-checked:bg-sky-500 rounded-full transition-colors" aria-hidden="true">
                  <span className={[
                    'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                    hydrationEnabled ? 'translate-x-4' : '',
                  ].join(' ')} />
                </span>
              </label>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Trackear cuándo se cambió el agua. Mascotas que comparten balde se registran juntas.
            </p>
          </div>
        </div>

        {hydrationEnabled && (
          <div className="flex flex-col gap-2 mt-2">
            {hydrationGroups.length === 0 ? (
              <button
                type="button"
                onClick={() =>
                  persistDraftAndNavigate(
                    `${ROUTES.WATER_GROUP_NEW}?returnTo=${encodeURIComponent('/pets/new')}`
                  )
                }
                className="flex items-center gap-2 rounded-xl border border-dashed border-sky-300 px-3 py-3 text-sm text-sky-700 hover:bg-sky-50 transition-colors text-left"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Crear primer balde de agua
              </button>
            ) : (
              <>
                {hydrationGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setSelectedHydrationGroupId(g.id)}
                    className={[
                      'flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors',
                      selectedHydrationGroupId === g.id
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <span className="flex-1">
                      <span className="block text-sm font-medium text-slate-900">{g.name}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Cada {g.hydrationIntervalHours}h
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={[
                        'ml-2 h-4 w-4 rounded-full shrink-0',
                        selectedHydrationGroupId === g.id ? 'bg-sky-600' : 'border-2 border-slate-300',
                      ].join(' ')}
                    />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    persistDraftAndNavigate(
                      `${ROUTES.WATER_GROUP_NEW}?returnTo=${encodeURIComponent('/pets/new')}`
                    )
                  }
                  className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors text-left"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Crear balde nuevo
                </button>
              </>
            )}
          </div>
        )}
      </Card>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={() => {
            sessionStorage.removeItem(PETFORM_DRAFT_KEY);
            navigate(ROUTES.PETS);
          }}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={submitting}
          disabled={!selectedGroupId}
        >
          Crear mascota
        </Button>
      </div>
    </form>
  );
};

export default PetForm;
